import {
  Box,
  Button,
  Divider,
  Popover,
  Stack,
  Typography,
} from '@mui/material';
import { ArrowSquareOut, VideoCamera } from '@phosphor-icons/react';
import { Api } from '../../api';
import { addMinutes, parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import pluralize from 'pluralize';

interface AppointmentPopupProps {
  appointment: Api.Appointment.V_ClinicianSub | null;
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  onReschedule?: (appointment: Api.Appointment.V_ClinicianSub) => void;
  timezone: string;
}

export default function AppointmentPopup({
  appointment,
  anchorEl,
  open,
  onClose,
  onReschedule,
  timezone,
}: AppointmentPopupProps) {
  if (!appointment) return null;

  const hasZoom = !!appointment.zoomLink;

  const appointmentDate = parseISO(appointment.startAt);
  const formattedDate = formatInTimeZone(
    appointmentDate,
    timezone,
    'EEEE, d MMMM yyyy',
  );
  const startTime = formatInTimeZone(appointmentDate, timezone, 'HH:mm');
  const endDate = addMinutes(appointmentDate, appointment.duration);
  const endTime = formatInTimeZone(endDate, timezone, 'HH:mm');

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
      transformOrigin={{ vertical: 'center', horizontal: 'left' }}
      sx={{ ml: 1 }}
    >
      <Box
        sx={{
          p: 2,
          minWidth: 280,
          backgroundColor: 'background.paper',
          borderRadius: 1,
          boxShadow: 3,
          borderTop: '4px solid',
          borderTopColor: 'primary.main',
        }}
      >
        <Stack spacing={1}>
          <Box>
            <Typography variant="h6">
              {appointment.user.name || appointment.user.id}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              2 week of program
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
            >{`${appointment.userAppointmentCount ?? 0} ${pluralize('appointment', appointment.userAppointmentCount ?? 0)} with you`}</Typography>
            <Button
              size="small"
              startIcon={<ArrowSquareOut size={16} />}
              component="a"
              href={`/users/${appointment.user.id}`}
              target="_blank"
            >
              Open Profile
            </Button>
          </Box>
          <Divider />
          <Box>
            <Typography variant="subtitle2" color="text.primary">
              {formattedDate}
            </Typography>
            <Typography variant="subtitle2" color="text.primary">
              {startTime} - {endTime}
            </Typography>
            {hasZoom && (
              <Button
                variant="contained"
                startIcon={<VideoCamera size={16} />}
                size="small"
                fullWidth
                sx={{ mt: 1 }}
                onClick={() => {
                  const url = appointment.zoomLink as string;
                  window.open(url, '_blank');
                }}
              >
                Join with Zoom
              </Button>
            )}
          </Box>
          <Divider />
          <Box>
            <Button
              variant="outlined"
              size="small"
              onClick={() => appointment && onReschedule?.(appointment)}
            >
              Reschedule
            </Button>
          </Box>
        </Stack>
      </Box>
    </Popover>
  );
}
